import React from 'react'
import { ArrowRight, Check, ShoppingBag } from 'lucide-react'
import { MAX_THEMES } from '@adthlete/common/utils/dealPricing'
import type { MarketplaceProfileData } from './types'

/**
 * Setting up a Content Shop, the same five steps everywhere: the athlete's My page and home, the
 * manager's view of an athlete's shop, and anything we market. Worked out from the shop itself
 * (the marketplace profile), so every app agrees on what's done.
 */
export type ShopStepId = 'profile' | 'socials' | 'series' | 'prices' | 'open'

export type ShopStep = {
  id: ShopStepId
  title: string
  /** Why it matters, or how far along it is */
  detail: string
  done: boolean
}

/** A shop is ready to sell with this many series (up to MAX_THEMES) */
export const SHOP_MIN_SERIES = 3

export function shopSetupSteps(data: MarketplaceProfileData): ShopStep[] {
  const series = data.themes.length
  const withExample = data.themes.filter((t) => t.example?.url).length
  // Linked by a login or by handle (enrichment brings in the numbers and posts either way)
  const connected = data.channels.length
  const a = data.athlete
  const missing = [
    !a.avatar && 'photo',
    !a.bio && 'bio',
    !a.sports.length && 'sport',
    !a.school && 'school',
  ].filter(Boolean) as string[]
  return [
    {
      id: 'profile',
      title: 'Your profile',
      detail: missing.length
        ? `Add a ${missing.join(', ')}. The first thing brands see`
        : 'Photo, bio, sport and school',
      done: !missing.length,
    },
    {
      id: 'socials',
      title: 'Add your socials',
      detail: connected
        ? `${connected} linked. Your posts are the pictures in your shop`
        : 'Your posts become the pictures in your shop, with your numbers',
      done: connected > 0,
    },
    {
      // Picking the series and showing each one: one screen, one step
      id: 'series',
      title: 'Build your series',
      detail:
        series < SHOP_MIN_SERIES
          ? `${series} of ${SHOP_MIN_SERIES} picked. Pick the content you post again and again, with an example of each`
          : withExample < series
            ? `${withExample} of ${series} have an example. Brands buy what they can see${
                connected ? '' : ': paste links to posts, or connect your socials to pick them'
              }`
            : `${series} series, each with an example`,
      done: series >= SHOP_MIN_SERIES && withExample === series,
    },
    {
      id: 'prices',
      title: 'Set your prices',
      detail: data.rateCard
        ? `Every series has a price${
            data.rateCard.items?.length
              ? `, plus ${data.rateCard.items.length} item${data.rateCard.items.length === 1 ? '' : 's'} of your own`
              : '. Add your own items too'
          }`
        : 'So brands can book in one tap. Add your own items too',
      done: !!data.rateCard,
    },
    {
      id: 'open',
      title: 'Open your shop',
      detail:
        data.availability.published && data.availability.openToOffers
          ? 'Brands can find you and book'
          : 'Claim your shop link and let brands book you',
      done: !!data.availability.published && data.availability.openToOffers,
    },
  ]
}

export const shopSetupProgress = (data: MarketplaceProfileData) => {
  const steps = shopSetupSteps(data)
  const done = steps.filter((s) => s.done).length
  return { steps, done, total: steps.length, ready: done === steps.length }
}

/** What a step's button does in this app, or null when it's someone else's to do */
export type ShopStepAction = { label: string; onClick: () => void } | { note: string } | null

/**
 * The checklist: "Set up your Content Shop · 3 of 5", each step with its button. `name` is
 * left out for the athlete ("your shop") and given for a manager ("Emma's shop").
 */
export const ShopSetupChecklist: React.FC<{
  data: MarketplaceProfileData
  name?: string
  actionFor: (step: ShopStep) => ShopStepAction
  /** Shown when every step is done (e.g. Share my shop) */
  doneAction?: React.ReactNode
}> = ({ data, name, actionFor, doneAction }) => {
  const { steps, done, total, ready } = shopSetupProgress(data)
  const whose = name ? `${name}'s` : 'your'
  return (
    <section className="rounded-2xl border border-[#E5E7EB] bg-[#FFFFFF] p-4 sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#C0FD72] text-[#0F172A]">
            <ShoppingBag className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <p className="font-bold text-[#111827]">
              {ready
                ? `${name ? `${name}'s` : 'Your'} Content Shop is open`
                : `Set up ${whose} Content Shop`}
            </p>
            <p className="text-sm text-[#6B7280]">
              {ready
                ? 'Brands can see the content and book it in a tap.'
                : `${done} of ${total} done. Brands shop the content, so each step sells it.`}
            </p>
          </div>
        </div>
        {ready ? doneAction : null}
      </div>
      <div
        className="mt-3 h-2 overflow-hidden rounded-full bg-[#F3F4F6]"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={done}
        aria-label="Content Shop set-up"
      >
        <div
          className="h-full rounded-full bg-[#84CC16] transition-all"
          style={{ width: `${(done / total) * 100}%` }}
        />
      </div>
      <ol className="mt-4 divide-y divide-[#F3F4F6]">
        {steps.map((step, i) => {
          const action = actionFor(step)
          return (
            <li key={step.id} className="flex flex-wrap items-center gap-3 py-2.5">
              <span
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                  step.done ? 'bg-[#84CC16] text-[#FFFFFF]' : 'bg-[#F3F4F6] text-[#374151]'
                }`}
              >
                {step.done ? <Check className="h-4 w-4" /> : i + 1}
              </span>
              <span className="min-w-0 flex-1">
                <span
                  className={`block text-sm font-semibold ${step.done ? 'text-[#374151]' : 'text-[#111827]'}`}
                >
                  {step.title}
                </span>
                <span className="block text-xs text-[#6B7280]">{step.detail}</span>
              </span>
              {action && 'onClick' in action ? (
                step.done ? (
                  // Done steps stay open to change
                  <button
                    type="button"
                    onClick={action.onClick}
                    className="inline-flex h-9 shrink-0 items-center gap-1 rounded-lg border border-[#E5E7EB] px-3 text-sm font-semibold text-[#111827] hover:bg-[#F9FAFB]"
                  >
                    Edit
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={action.onClick}
                    className="inline-flex h-9 shrink-0 items-center gap-1 rounded-lg bg-[#0F172A] px-3 text-sm font-semibold text-[#FFFFFF] hover:bg-[#1E293B]"
                  >
                    {action.label} <ArrowRight className="h-4 w-4" />
                  </button>
                )
              ) : action && 'note' in action && !step.done ? (
                <span className="shrink-0 text-xs text-[#6B7280]">{action.note}</span>
              ) : null}
            </li>
          )
        })}
      </ol>
    </section>
  )
}

export default ShopSetupChecklist
